import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, CheckCheck, FileText, DollarSign, ClipboardList, AlertCircle, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { notificationService } from '@/lib/api';

const ICON_MAP = {
  payment_received: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  document_request: { icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-100' },
  certificate_request: { icon: ClipboardList, color: 'text-amber-600', bg: 'bg-amber-100' },
  payment_report: { icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-100' },
};

const NotificationBell = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const pollRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        notificationService.list({ limit: 20 }),
        notificationService.getUnreadCount()
      ]);
      setNotifications(listRes.data.notifications || []);
      setUnreadCount(countRes.data.unread_count || 0);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    // Poll every 10 seconds for new notifications
    pollRef.current = setInterval(loadNotifications, 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error('Error marking read:', err); }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) { console.error('Error marking all read:', err); }
    finally { setLoading(false); }
  };

  const handleOpen = (notif) => {
    if (!notif.is_read) handleMarkRead(notif.id);
    if (onNavigate) {
      setIsOpen(false);
      onNavigate(notif);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) loadNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-semibold text-slate-900 text-sm">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleMarkAllRead} disabled={loading} className="text-xs h-7 text-teal-600 hover:text-teal-700" data-testid="mark-all-read">
                  <CheckCheck className="w-3.5 h-3.5 mr-1" />Marcar todas
                </Button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Sin notificaciones</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const config = ICON_MAP[notif.type] || ICON_MAP.payment_report;
                const Icon = config.icon;
                return (
                  <div
                    key={notif.id}
                    className={`group flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-teal-50/40' : ''}`}
                    onClick={() => handleOpen(notif)}
                    role="button"
                    title="Ir a la solicitud"
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${!notif.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{notif.title}</p>
                        <span className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0">{timeAgo(notif.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 mt-1">
                      {!notif.is_read && <div className="w-2 h-2 rounded-full bg-teal-500" />}
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 transition-colors" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
